import { useState } from "react";
import { Calculator, ChevronLeft, Edit3, Save, Loader2, Plus, Trash2 } from "lucide-react";
import { DEFAULT_SERVICIO_GRUPO, DEFAULT_SERVICIO_INDIVIDUAL } from "./PreciosOcupacion";

const TIPO_GASTO_OPTIONS = [
  { value: "fijos", label: "Fijos" },
  { value: "variable", label: "Variable" },
  { value: "otros", label: "Otros" },
];

function InputField({ label, value, onChange, type = "number", suffix, placeholder, min, max }) {
  const displayValue = value === 0 || value === "" ? "" : value;

  const clamp = (v) => {
    if (v === "") return "";
    let n = parseFloat(v);
    if (isNaN(n)) return 0;
    if (min !== undefined && n < min) n = min;
    if (max !== undefined && n > max) n = max;
    return n;
  };

  return (
    <div className="flex-1 min-w-0">
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      <div className="relative">
        <input
          type={type}
          value={displayValue}
          onChange={(e) => {
            const val = e.target.value;
            if (type === "number") {
              onChange(clamp(val));
            } else {
              onChange(val);
            }
          }}
          placeholder={placeholder || "0"}
          min={min}
          max={max}
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none"
        />
        {suffix && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
            {suffix}
          </span>
        )}
      </div>
    </div>
  );
}

function Section({ title, children, color = "slate" }) {
  const styles = {
    slate:   { border: "border-l-slate-400",   headerBg: "bg-slate-50",   text: "text-slate-700" },
    teal:    { border: "border-l-teal-400",    headerBg: "bg-teal-50/60", text: "text-teal-700" },
    cyan:    { border: "border-l-cyan-400",    headerBg: "bg-cyan-50/60", text: "text-cyan-700" },
    sky:     { border: "border-l-sky-400",     headerBg: "bg-sky-50/60",  text: "text-sky-700" },
    blue:    { border: "border-l-blue-400",    headerBg: "bg-blue-50/60", text: "text-blue-700" },
    emerald: { border: "border-l-emerald-400", headerBg: "bg-emerald-50/60", text: "text-emerald-700" },
  };
  const s = styles[color] || styles.slate;

  return (
    <div className={`bg-white rounded-xl border border-gray-200 border-l-4 ${s.border} overflow-hidden`}>
      <div className={`px-4 py-3 border-b border-gray-100 ${s.headerBg}`}>
        <h3 className={`font-semibold text-sm ${s.text}`}>{title}</h3>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function ResultRow({ label, value, highlight, negative }) {
  const numValue = typeof value === "number" ? value : 0;
  return (
    <div className={`flex justify-between items-center py-2 ${highlight ? "font-semibold" : ""}`}>
      <span className={highlight ? "text-gray-900" : "text-gray-600 text-sm"}>{label}</span>
      <span className={`font-mono ${negative ? "text-red-500" : highlight ? "text-brand font-bold" : "text-gray-900"}`}>
        {numValue.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}€
      </span>
    </div>
  );
}

const SCENARIOS = [
  { key: "actual", label: "Actual", color: "gray" },
  { key: "deseado", label: "Deseado", color: "brand" },
  { key: "max", label: "Máximo", color: "emerald" },
];

export default function SimulationEditor({ state, setState, onSave, onCancel, saving }) {
  const [activeScenario, setActiveScenario] = useState("max");
  const update = (field, value) => setState((s) => ({ ...s, [field]: value }));
  const num = (v) => (typeof v === "number" ? v : parseFloat(v) || 0);

  const addServicioGrupo = () => {
    setState((s) => ({
      ...s,
      servicios_grupo: [...s.servicios_grupo, { ...DEFAULT_SERVICIO_GRUPO, nombre: `Grupo ${s.servicios_grupo.length + 1}` }],
    }));
  };
  const updateServicioGrupo = (idx, field, value) => {
    setState((s) => {
      const servicios_grupo = [...s.servicios_grupo];
      servicios_grupo[idx] = { ...servicios_grupo[idx], [field]: value };
      return { ...s, servicios_grupo };
    });
  };
  const removeServicioGrupo = (idx) => {
    setState((s) => ({ ...s, servicios_grupo: s.servicios_grupo.filter((_, i) => i !== idx) }));
  };

  const addServicioIndividual = () => {
    setState((s) => ({
      ...s,
      servicios_individuales: [...s.servicios_individuales, { ...DEFAULT_SERVICIO_INDIVIDUAL, nombre: `Individual ${s.servicios_individuales.length + 1}` }],
    }));
  };
  const updateServicioIndividual = (idx, field, value) => {
    setState((s) => {
      const servicios_individuales = [...s.servicios_individuales];
      servicios_individuales[idx] = { ...servicios_individuales[idx], [field]: value };
      return { ...s, servicios_individuales };
    });
  };
  const removeServicioIndividual = (idx) => {
    setState((s) => ({ ...s, servicios_individuales: s.servicios_individuales.filter((_, i) => i !== idx) }));
  };

  const addEmpleado = () => {
    setState((s) => ({ ...s, empleados: [...s.empleados, { nombre: "", horas: "", salario: "" }] }));
  };
  const updateEmpleado = (idx, field, value) => {
    setState((s) => {
      const empleados = [...s.empleados];
      empleados[idx] = { ...empleados[idx], [field]: value };
      return { ...s, empleados };
    });
  };
  const removeEmpleado = (idx) => {
    setState((s) => ({ ...s, empleados: s.empleados.filter((_, i) => i !== idx) }));
  };

  const addGasto = () => {
    setState((s) => ({ ...s, gastos: [...s.gastos, { concepto: "", tipo: "fijos", importe: "" }] }));
  };
  const updateGasto = (idx, field, value) => {
    setState((s) => {
      const gastos = [...s.gastos];
      gastos[idx] = { ...gastos[idx], [field]: value };
      return { ...s, gastos };
    });
  };
  const removeGasto = (idx) => {
    setState((s) => ({ ...s, gastos: s.gastos.filter((_, i) => i !== idx) }));
  };

  const calcGrupo = (s) => {
    const horasTrabajo = (num(s.sesiones_semana) * num(s.minutos)) / 60;
    const sesionesCliente = num(s.sesiones_cliente) || 2.2;
    const clientesMax = (num(s.sesiones_semana) * num(s.personas)) / sesionesCliente;
    const clientesActual = num(s.clientes_actuales);
    const clientesDeseado = clientesMax * (num(s.pct_deseado) / 100);
    const cuota = num(s.cuota);
    const profesionales = num(s.horas_prof) > 0 ? horasTrabajo / num(s.horas_prof) : 0;
    return {
      horasTrabajo, clientesMax, clientesActual, clientesDeseado, profesionales,
      ingresosMax: clientesMax * cuota,
      ingresosActual: clientesActual * cuota,
      ingresosDeseado: clientesDeseado * cuota,
    };
  };

  const calcIndividual = (s) => {
    const horas = (num(s.sesiones_semana) * num(s.minutos)) / 60;
    const profesionales = num(s.horas_prof) > 0 ? horas / num(s.horas_prof) : 0;
    const ingresos = num(s.cuota) * num(s.sesiones_semana) * num(state.semanas_mes);
    return { horas, ingresos, profesionales };
  };

  const gruposCalc = (state.servicios_grupo || []).map(calcGrupo);
  const individualesCalc = (state.servicios_individuales || []).map(calcIndividual);

  const totalGrupoHoras = gruposCalc.reduce((sum, c) => sum + c.horasTrabajo, 0);
  const totalIndHoras = individualesCalc.reduce((sum, c) => sum + c.horas, 0);
  const totalHorasTrabajo = totalGrupoHoras + totalIndHoras;
  const totalIndIngresos = individualesCalc.reduce((sum, c) => sum + c.ingresos, 0);
  const totalProfesionales = gruposCalc.reduce((sum, c) => sum + c.profesionales, 0)
    + individualesCalc.reduce((sum, c) => sum + c.profesionales, 0);

  const empleadosValidos = state.empleados.filter(e => e.nombre?.trim() && num(e.salario) > 0);
  const totalSalarios = empleadosValidos.reduce((sum, e) => sum + num(e.salario), 0);
  const totalSS = totalSalarios * (num(state.seg_social_pct) / 100);
  const costePersonal = totalSalarios + totalSS;
  const totalHorasContratadas = empleadosValidos.reduce((sum, e) => sum + num(e.horas), 0);

  const gastosValidos = state.gastos.filter(g => g.concepto?.trim() && num(g.importe) > 0);
  const gastosFijos = gastosValidos.filter(g => g.tipo === "fijos").reduce((sum, g) => sum + num(g.importe), 0);
  const gastosVariable = gastosValidos.filter(g => g.tipo === "variable").reduce((sum, g) => sum + num(g.importe), 0);
  const gastosOtros = gastosValidos.filter(g => g.tipo === "otros").reduce((sum, g) => sum + num(g.importe), 0);
  const totalGastosFijos = gastosFijos + gastosVariable + gastosOtros;
  const totalGastos = costePersonal + totalGastosFijos;
  const ivaGastos = totalGastosFijos * 0.21;

  const calcRentabilidad = (grupoIngresosFn) => {
    const grupoIngresos = gruposCalc.reduce((sum, c) => sum + grupoIngresosFn(c), 0);
    const totalIngresos = grupoIngresos + totalIndIngresos;
    const ivaGrupos = (state.servicios_grupo || []).reduce((sum, s, i) => {
      return sum + grupoIngresosFn(gruposCalc[i]) * (num(s.iva) / 100);
    }, 0);
    const ivaInd = (state.servicios_individuales || []).reduce((sum, s, i) => {
      return sum + individualesCalc[i].ingresos * (num(s.iva) / 100);
    }, 0);
    const iva = (ivaGrupos + ivaInd) - ivaGastos;
    const resultado = totalIngresos - totalGastos - iva;
    const reserva = resultado * 0.1;
    const rentabilidad = resultado - reserva;
    const porcentaje = totalIngresos > 0 ? (rentabilidad / totalIngresos) * 100 : 0;
    return { totalIngresos, iva, resultado, reserva, rentabilidad, porcentaje };
  };

  const escActual = calcRentabilidad((c) => c.ingresosActual);
  const escDeseado = calcRentabilidad((c) => c.ingresosDeseado);
  const escMax = calcRentabilidad((c) => c.ingresosMax);

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <button onClick={onCancel} className="flex items-center gap-1 text-gray-500 hover:text-gray-700 text-sm">
            <ChevronLeft size={18} /> Volver
          </button>
          <div className="flex-1 flex items-center gap-2">
            <Edit3 size={16} className="text-gray-400" />
            <input
              type="text"
              value={state.name}
              onChange={(e) => update("name", e.target.value)}
              className="flex-1 text-lg font-bold text-gray-900 bg-transparent border-b-2 border-transparent hover:border-gray-200 focus:border-brand outline-none px-1"
              placeholder="Nombre de la simulación"
            />
          </div>
          <div className="flex gap-2">
            <button onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
              Cancelar
            </button>
            <button
              onClick={onSave}
              disabled={saving}
              className="px-4 py-2 text-sm bg-brand text-white rounded-lg hover:bg-brand-dark flex items-center gap-1.5 disabled:opacity-50"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              Guardar
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        {/* Left Column - Inputs */}
        <div className="xl:col-span-2 space-y-4">
          <Section title="Configuración General" color="slate">
            <div className="max-w-xs">
              <InputField label="% Seguridad Social" value={state.seg_social_pct} onChange={(v) => update("seg_social_pct", v)} suffix="%" min={0} max={100} />
            </div>
          </Section>

          <Section title="Servicios en Grupo" color="teal">
            {(state.servicios_grupo || []).length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">Sin servicios de grupo añadidos</p>
            ) : (
              <div className="space-y-4">
                {(state.servicios_grupo || []).map((servicio, idx) => {
                  const calc = gruposCalc[idx] || { horasTrabajo: 0, clientesMax: 0, clientesActual: 0, clientesDeseado: 0, ingresosMax: 0, ingresosActual: 0, ingresosDeseado: 0, profesionales: 0 };
                  return (
                    <div key={idx} className={`p-3 rounded-lg border bg-teal-50 border-teal-200`}>
                      <div className="flex items-center gap-2 mb-3">
                        <input
                          type="text"
                          value={servicio.nombre}
                          onChange={(e) => updateServicioGrupo(idx, "nombre", e.target.value)}
                          className="flex-1 px-2 py-1 text-sm font-semibold bg-transparent border-b border-transparent hover:border-gray-300 focus:border-brand outline-none"
                          placeholder="Nombre del servicio"
                        />
                        {(state.servicios_grupo || []).length > 1 && (
                          <button onClick={() => removeServicioGrupo(idx)} className="p-1 text-red-500 hover:bg-red-50 rounded">
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          <InputField label="IVA %" value={servicio.iva} onChange={(v) => updateServicioGrupo(idx, "iva", v)} min={0} max={100} />
                          <InputField label="Personas/grupo" value={servicio.personas} onChange={(v) => updateServicioGrupo(idx, "personas", v)} min={0} />
                          <InputField label="Sesiones/sem" value={servicio.sesiones_semana} onChange={(v) => updateServicioGrupo(idx, "sesiones_semana", v)} min={0} />
                          <InputField label="Ses/sem cliente" value={servicio.sesiones_cliente} onChange={(v) => updateServicioGrupo(idx, "sesiones_cliente", v)} min={0} />
                          <InputField label="Min/sesión" value={servicio.minutos} onChange={(v) => updateServicioGrupo(idx, "minutos", v)} min={0} />
                          <InputField label="Cuota €/mes" value={servicio.cuota} onChange={(v) => updateServicioGrupo(idx, "cuota", v)} min={0} />
                          <InputField label="Horas/prof/sem" value={servicio.horas_prof} onChange={(v) => updateServicioGrupo(idx, "horas_prof", v)} min={0} />
                        </div>
                      <div className="grid grid-cols-2 gap-2 mt-2 pt-2 border-t border-gray-100">
                        <InputField label="Clientes actuales" value={servicio.clientes_actuales} onChange={(v) => updateServicioGrupo(idx, "clientes_actuales", v)} min={0} />
                        <InputField label="% ocupación deseado" value={servicio.pct_deseado} onChange={(v) => updateServicioGrupo(idx, "pct_deseado", v)} suffix="%" min={0} max={100} />
                      </div>
                      <div className="mt-2 pt-2 border-t border-gray-100 space-y-1 text-xs">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Horas: <span className="font-semibold text-gray-700">{calc.horasTrabajo.toFixed(1)}h</span> · Profesionales necesarios: <span className="font-semibold text-gray-700">{calc.profesionales.toFixed(1)}</span></span>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <div className="p-1.5 bg-white rounded text-center">
                            <p className="text-gray-400 text-[10px]">Actual</p>
                            <p className="font-semibold text-gray-600">{Math.round(calc.clientesActual)} <span className="hidden sm:inline">clientes</span><span className="sm:hidden">cl.</span></p>
                            <p className="font-semibold text-gray-600">{calc.ingresosActual.toFixed(0)}€</p>
                          </div>
                          <div className="p-1.5 bg-brand/5 rounded text-center">
                            <p className="text-brand text-[10px]">Deseado ({num(servicio.pct_deseado)}%)</p>
                            <p className="font-semibold text-brand">{Math.round(calc.clientesDeseado)} <span className="hidden sm:inline">clientes</span><span className="sm:hidden">cl.</span></p>
                            <p className="font-semibold text-brand">{calc.ingresosDeseado.toFixed(0)}€</p>
                          </div>
                          <div className="p-1.5 bg-emerald-50 rounded text-center">
                            <p className="text-emerald-500 text-[10px]">Máximo (100%)</p>
                            <p className="font-semibold text-emerald-600">{Math.round(calc.clientesMax)} <span className="hidden sm:inline">clientes</span><span className="sm:hidden">cl.</span></p>
                            <p className="font-semibold text-emerald-600">{calc.ingresosMax.toFixed(0)}€</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <button onClick={addServicioGrupo} className="mt-3 w-full py-2 text-sm text-brand border border-brand/30 rounded-lg hover:bg-brand-light flex items-center justify-center gap-1.5">
              <Plus size={16} /> Añadir servicio de grupo
            </button>
            {(state.servicios_grupo || []).length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-100 text-xs font-semibold">
                <div className="text-gray-700 mb-1">Total horas: {totalGrupoHoras.toFixed(1)}h</div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="text-center text-gray-600">Actual: {gruposCalc.reduce((s, c) => s + c.ingresosActual, 0).toFixed(0)}€</div>
                  <div className="text-center text-brand">Deseado: {gruposCalc.reduce((s, c) => s + c.ingresosDeseado, 0).toFixed(0)}€</div>
                  <div className="text-center text-emerald-600">Máximo: {gruposCalc.reduce((s, c) => s + c.ingresosMax, 0).toFixed(0)}€</div>
                </div>
              </div>
            )}
          </Section>

          <Section title="Sesiones Individuales" color="cyan">
            {(state.servicios_individuales || []).length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">Sin servicios individuales añadidos</p>
            ) : (
              <div className="space-y-4">
                {(state.servicios_individuales || []).map((servicio, idx) => {
                  const calc = individualesCalc[idx] || { horas: 0, ingresos: 0, profesionales: 0 };
                  return (
                    <div key={idx} className={`p-3 rounded-lg border bg-cyan-50 border-cyan-200`}>
                      <div className="flex items-center gap-2 mb-3">
                        <input
                          type="text"
                          value={servicio.nombre}
                          onChange={(e) => updateServicioIndividual(idx, "nombre", e.target.value)}
                          className="flex-1 px-2 py-1 text-sm font-semibold bg-transparent border-b border-transparent hover:border-gray-300 focus:border-brand outline-none"
                          placeholder="Nombre del servicio"
                        />
                        {(state.servicios_individuales || []).length > 1 && (
                          <button onClick={() => removeServicioIndividual(idx)} className="p-1 text-red-500 hover:bg-red-50 rounded">
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                        <InputField label="IVA %" value={servicio.iva} onChange={(v) => updateServicioIndividual(idx, "iva", v)} min={0} max={100} />
                        <InputField label="Sesiones/sem" value={servicio.sesiones_semana} onChange={(v) => updateServicioIndividual(idx, "sesiones_semana", v)} min={0} />
                        <InputField label="Min/sesión" value={servicio.minutos} onChange={(v) => updateServicioIndividual(idx, "minutos", v)} min={0} />
                        <InputField label="€/sesión" value={servicio.cuota} onChange={(v) => updateServicioIndividual(idx, "cuota", v)} min={0} />
                        <InputField label="Horas/prof/sem" value={servicio.horas_prof} onChange={(v) => updateServicioIndividual(idx, "horas_prof", v)} min={0} />
                      </div>
                      {num(servicio.sesiones_semana) > 0 && (
                        <div className="mt-2 pt-2 border-t border-gray-100 text-xs text-gray-500 flex gap-4">
                          <span>Horas: <span className="font-semibold text-gray-700">{calc.horas.toFixed(1)}h</span></span>
                          <span>Profesionales necesarios: <span className="font-semibold text-gray-700">{calc.profesionales.toFixed(1)}</span></span>
                          <span>Ingresos: <span className="font-semibold text-emerald-600">{calc.ingresos.toFixed(0)}€/mes</span></span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            <button onClick={addServicioIndividual} className="mt-3 w-full py-2 text-sm text-brand border border-brand/30 rounded-lg hover:bg-brand-light flex items-center justify-center gap-1.5">
              <Plus size={16} /> Añadir servicio individual
            </button>
            {(state.servicios_individuales || []).length > 0 && totalIndHoras > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-100 text-xs font-semibold flex gap-4">
                <span className="text-gray-700">Total: {totalIndHoras.toFixed(1)}h</span>
                <span className="text-emerald-600">{totalIndIngresos.toFixed(0)}€</span>
              </div>
            )}
          </Section>

          <Section title="Empleados" color="sky">
            {state.empleados.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">Sin empleados añadidos</p>
            ) : (
              <div className="space-y-2">
                {state.empleados.map((emp, idx) => {
                  const isValid = emp.nombre?.trim() && num(emp.salario) > 0;
                  const ss = num(emp.salario) * (num(state.seg_social_pct) / 100);
                  return (
                    <div key={idx} className={`p-3 rounded-lg border ${isValid ? "bg-sky-50/40 border-sky-100" : "bg-gray-50 border-gray-100"}`}>
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                        <input type="text" value={emp.nombre} onChange={(e) => updateEmpleado(idx, "nombre", e.target.value)} placeholder="Nombre" className="px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:border-brand" />
                        <input type="number" min="0" value={emp.horas === "" ? "" : emp.horas} onChange={(e) => { const v = e.target.value; updateEmpleado(idx, "horas", v === "" ? "" : Math.max(0, parseFloat(v) || 0)); }} placeholder="Horas/sem" className="px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:border-brand" />
                        <input type="number" min="0" value={emp.salario === "" ? "" : emp.salario} onChange={(e) => { const v = e.target.value; updateEmpleado(idx, "salario", v === "" ? "" : Math.max(0, parseFloat(v) || 0)); }} placeholder="Salario €" className="px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:border-brand" />
                        <div className="flex items-center gap-2">
                          <span className="flex-1 text-sm text-gray-500">SS: {ss > 0 ? `${ss.toFixed(0)}€` : "-"}</span>
                          <button onClick={() => removeEmpleado(idx)} className="p-1.5 text-red-500 hover:bg-red-50 rounded"><Trash2 size={16} /></button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <button onClick={addEmpleado} className="mt-3 w-full py-2 text-sm text-brand border border-brand/30 rounded-lg hover:bg-brand-light flex items-center justify-center gap-1.5">
              <Plus size={16} /> Añadir empleado
            </button>
            {empleadosValidos.length > 0 && (
              <div className="mt-3 pt-3 border-t text-xs text-gray-500">
                {empleadosValidos.length} empleado(s) · Salarios: {totalSalarios.toFixed(0)}€ · SS: {totalSS.toFixed(0)}€
              </div>
            )}
          </Section>

          <Section title="Gastos" color="blue">
            {state.gastos.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">Sin gastos añadidos</p>
            ) : (
              <div className="space-y-2">
                {state.gastos.map((gasto, idx) => {
                  const isValid = gasto.concepto?.trim() && num(gasto.importe) > 0;
                  return (
                    <div key={idx} className={`p-3 rounded-lg border ${isValid ? "bg-blue-50/40 border-blue-100" : "bg-gray-50 border-gray-100"}`}>
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                        <input type="text" value={gasto.concepto} onChange={(e) => updateGasto(idx, "concepto", e.target.value)} placeholder="Concepto" className="sm:col-span-2 px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:border-brand" />
                        <select value={gasto.tipo} onChange={(e) => updateGasto(idx, "tipo", e.target.value)} className="px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:border-brand bg-white">
                          {TIPO_GASTO_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                        </select>
                        <div className="flex items-center gap-2">
                          <input type="number" min="0" value={gasto.importe === "" ? "" : gasto.importe} onChange={(e) => { const v = e.target.value; updateGasto(idx, "importe", v === "" ? "" : Math.max(0, parseFloat(v) || 0)); }} placeholder="€" className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:border-brand" />
                          <button onClick={() => removeGasto(idx)} className="p-1.5 text-red-500 hover:bg-red-50 rounded"><Trash2 size={16} /></button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <button onClick={addGasto} className="mt-3 w-full py-2 text-sm text-brand border border-brand/30 rounded-lg hover:bg-brand-light flex items-center justify-center gap-1.5">
              <Plus size={16} /> Añadir gasto
            </button>
            {gastosValidos.length > 0 && (
              <div className="mt-3 pt-3 border-t grid grid-cols-3 gap-2 text-xs">
                <div className="text-center p-2 bg-blue-50 rounded-lg"><p className="text-blue-600 font-semibold">{gastosFijos.toFixed(0)}€</p><p className="text-blue-500">Fijos</p></div>
                <div className="text-center p-2 bg-amber-50 rounded-lg"><p className="text-amber-600 font-semibold">{gastosVariable.toFixed(0)}€</p><p className="text-amber-500">Variable</p></div>
                <div className="text-center p-2 bg-gray-100 rounded-lg"><p className="text-gray-600 font-semibold">{gastosOtros.toFixed(0)}€</p><p className="text-gray-500">Otros</p></div>
              </div>
            )}
          </Section>
        </div>

        {/* Right Column - Results */}
        <div>
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden xl:sticky xl:top-4">
            <div className="px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-brand to-brand-dark">
              <h3 className="font-semibold text-white flex items-center gap-2"><Calculator size={18} /> Rentabilidad</h3>
            </div>

            <div className="flex border-b border-gray-100">
              {SCENARIOS.map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setActiveScenario(key)}
                  className={`flex-1 py-2.5 text-xs font-semibold transition-colors ${
                    activeScenario === key
                      ? "text-brand border-b-2 border-brand bg-brand/5"
                      : "text-gray-400 hover:text-gray-600"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {(() => {
              const esc = activeScenario === "actual" ? escActual : activeScenario === "deseado" ? escDeseado : escMax;
              const pct = esc.porcentaje;

              const grupoIncomeFn = activeScenario === "actual"
                ? (c) => c.ingresosActual
                : activeScenario === "deseado"
                ? (c) => c.ingresosDeseado
                : (c) => c.ingresosMax;
              const grupoBreakdown = (state.servicios_grupo || [])
                .map((s, i) => ({
                  nombre: s.nombre || "Servicio Grupo",
                  ingresos: grupoIncomeFn(gruposCalc[i] || {}),
                }))
                .filter((b) => b.ingresos > 0);
              const indBreakdown = (state.servicios_individuales || [])
                .map((s, i) => ({
                  nombre: s.nombre || "Servicio Individual",
                  ingresos: (individualesCalc[i] || {}).ingresos || 0,
                }))
                .filter((b) => b.ingresos > 0);
              const ingresosBreakdown = [...grupoBreakdown, ...indBreakdown];

              return (
                <div className="p-4 space-y-1">
                  <ResultRow label="Ingresos totales" value={esc.totalIngresos} highlight />
                  {ingresosBreakdown.length > 0 && (
                    <div className="pl-3 border-l-2 border-gray-100">
                      {ingresosBreakdown.map((b, i) => (
                        <ResultRow key={`ing-${i}`} label={b.nombre} value={b.ingresos} />
                      ))}
                    </div>
                  )}
                  <div className="border-t border-gray-100 my-2" />
                  <ResultRow label="Gastos totales" value={totalGastos} highlight />
                  <div className="pl-3 border-l-2 border-gray-100">
                    <ResultRow label="Coste personal" value={costePersonal} />
                    <ResultRow label="Gastos fijos" value={gastosFijos} />
                    <ResultRow label="Gastos variables" value={gastosVariable} />
                    <ResultRow label="Otros gastos" value={gastosOtros} />
                  </div>
                  <div className="border-t border-gray-100 my-2" />
                  <ResultRow label="IVA neto" value={esc.iva} />
                  <ResultRow label="Resultado" value={esc.resultado} negative={esc.resultado < 0} />
                  <ResultRow label="Reserva (10%)" value={esc.reserva} />
                  <div className="border-t border-gray-100 my-2" />
                  <ResultRow label="Rentabilidad" value={esc.rentabilidad} highlight negative={esc.rentabilidad < 0} />

                  <div className={`mt-4 p-3 rounded-lg text-center ${pct >= 20 ? "bg-emerald-50" : pct >= 10 ? "bg-amber-50" : "bg-red-50"}`}>
                    <p className={`text-3xl font-bold ${pct >= 20 ? "text-emerald-600" : pct >= 10 ? "text-amber-600" : "text-red-600"}`}>
                      {pct.toFixed(1)}%
                    </p>
                    <p className={`text-xs mt-1 ${pct >= 20 ? "text-emerald-600" : pct >= 10 ? "text-amber-600" : "text-red-600"}`}>
                      {pct >= 20 ? "Rentabilidad adecuada" : pct >= 10 ? "Rentabilidad justa" : "Revisar cuotas"}
                    </p>
                  </div>

                  <div className="grid grid-cols-3 gap-1.5 mt-4">
                    {[
                      { key: "actual", label: "Actual", esc: escActual },
                      { key: "deseado", label: "Deseado", esc: escDeseado },
                      { key: "max", label: "Máximo", esc: escMax },
                    ].map((s) => {
                      const p = s.esc.porcentaje;
                      const isActive = activeScenario === s.key;
                      return (
                        <button
                          key={s.key}
                          onClick={() => setActiveScenario(s.key)}
                          className={`p-2 rounded-lg text-center transition-all ${
                            isActive ? "ring-2 ring-brand/30" : "opacity-60 hover:opacity-100"
                          } ${p >= 20 ? "bg-emerald-50" : p >= 10 ? "bg-amber-50" : "bg-red-50"}`}
                        >
                          <p className="text-[10px] text-gray-500">{s.label}</p>
                          <p className={`text-sm font-bold ${p >= 20 ? "text-emerald-600" : p >= 10 ? "text-amber-600" : "text-red-600"}`}>
                            {p.toFixed(1)}%
                          </p>
                        </button>
                      );
                    })}
                  </div>

                  <div className="mt-4 pt-3 border-t border-gray-100 text-xs text-gray-500 space-y-1">
                    <div className="flex justify-between">
                      <span>Horas necesarias</span>
                      <span className="font-medium">{totalHorasTrabajo.toFixed(1)}h/sem</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Horas contratadas</span>
                      <span className={`font-medium ${totalHorasContratadas >= totalHorasTrabajo ? "text-emerald-600" : "text-red-500"}`}>
                        {totalHorasContratadas}h/sem
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Profesionales necesarios</span>
                      <span className="font-medium">{totalProfesionales.toFixed(1)}</span>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
}
